import { useAuth } from '@platform/auth-client';
import { CommandPalette, useBreakpoint, type Command } from '@platform/design-system';
import { useDirection, useTranslation } from '@platform/i18n';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { TypedClient } from '@platform/openapi';
import { useMemo, useState } from 'react';
import { NotificationBell } from '../features/notifications/NotificationBell.tsx';
import { UNNARROWED } from '../features/orders/order-filters.ts';
import { useOrderViewCounts } from '../features/orders/useOrderList.ts';
import { useOrderLookup } from '../features/orders/useOrderLookup.ts';
import { useShortcuts } from '../features/useShortcuts.ts';
import { HelpMenu } from './HelpMenu.tsx';
import {
  bottomNavigation,
  pageTitleKeyOf,
  titledDestinations,
  visibleDestinations,
  visibleNavigation,
  visiblePinned,
  badgeCountsOf,
  type NavBadgeCounts,
} from './navigation.ts';
import { NavItemLink, SidebarNav } from './SidebarNav.tsx';
import { useShell } from './shell.store.ts';
import { BrandLockup } from './BrandLockup.tsx';
import { TenantSwitcher } from './TenantSwitcher.tsx';
import { ThemeToggle } from './ThemeToggle.tsx';
import { useShopIdentity } from './useShopIdentity.ts';
import { UserMenu } from './UserMenu.tsx';
import styles from './Shell.module.css';
import { ORDERS_DEFAULTS } from '../app/search-params.ts';

const SIDEBAR_SHORTCUT = '[';

export const Shell = ({ api }: { api: TypedClient }) => {
  const { t } = useTranslation();
  useDirection();
  const auth = useAuth();
  const navigate = useNavigate();
  const isTablet = useBreakpoint('md');
  const isDesktop = useBreakpoint('lg');
  const expanded = useShell((state) => state.sidebarExpanded);
  const toggleSidebar = useShell((state) => state.toggleSidebar);
  const entries = useMemo(() => visibleNavigation(auth.hasRole), [auth]);
  const pinned = useMemo(() => visiblePinned(auth.hasRole), [auth]);
  const labelled = expanded && isDesktop;
  const collapseLabel = expanded ? t('shell.collapse') : t('shell.expand');
  const shop = useShopIdentity(api);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const viewCounts = useOrderViewCounts(api, UNNARROWED).data;
  const badges: NavBadgeCounts = badgeCountsOf(viewCounts);

  useShortcuts({
    [SIDEBAR_SHORTCUT]: () => {
      if (isDesktop) toggleSidebar();
    },
  });

  const [search, setSearch] = useState('');
  const lookup = useOrderLookup(api, search);
  const foundOrders: Command[] = lookup.orders.map((order) => ({
    id: order.id,
    label: t('shell.orderResult', { number: order.number, customer: order.customer.name }),
    run: () => {
      void navigate({ to: '/orders', search: { ...ORDERS_DEFAULTS, order: order.id } });
    },
  }));

  const destinations = useMemo(() => visibleDestinations(auth.hasRole), [auth]);
  const titleKey = pageTitleKeyOf(titledDestinations(destinations), pathname);
  const commands = useMemo<Command[]>(
    () =>
      destinations.map((item) => ({
        id: item.id,
        label: t(item.labelKey),
        run: () => {
          void navigate({ to: item.to });
        },
      })),
    [destinations, navigate, t],
  );

  return (
    <div className={[styles['layout'], labelled ? styles['expanded'] : ''].join(' ')}>
      <aside className={styles['sidebar']} aria-label={t('shell.menu')} data-testid="sidebar">
        <BrandLockup surface="brand" />
        <TenantSwitcher variant="card" shop={shop} />
        <SidebarNav entries={entries} pinned={pinned} labelled={labelled} badges={badges} />
        <HelpMenu />
      </aside>
      {isDesktop ? (
        <button
          type="button"
          className={styles['collapseHandle']}
          aria-label={collapseLabel}
          aria-keyshortcuts={SIDEBAR_SHORTCUT}
          title={`${collapseLabel} (${SIDEBAR_SHORTCUT})`}
          onClick={toggleSidebar}
        >
          {expanded ? (
            <ChevronLeft size={14} aria-hidden="true" className="flip-inline" />
          ) : (
            <ChevronRight size={14} aria-hidden="true" className="flip-inline" />
          )}
        </button>
      ) : null}
      <header className={styles['topbar']} data-testid="topbar">
        {isTablet ? (
          <div className={styles['crumb']}>
            <span className={styles['crumbShop']}>{shop.name}</span>
            {titleKey === null ? null : <span className={styles['crumbPage']}>{t(titleKey)}</span>}
          </div>
        ) : (
          <TenantSwitcher variant="compact" shop={shop} />
        )}
        <div className={styles['search']}>
          {isTablet ? (
            <CommandPalette
              commands={commands}
              labels={{
                trigger: t('shell.search'),
                placeholder: t('shell.searchPlaceholder'),
                title: t('shell.commands'),
                empty: t('shell.noResults'),
                busy: t('shell.searching'),
              }}
              found={
                lookup.active
                  ? { title: t('shell.ordersFound'), commands: foundOrders, busy: lookup.busy }
                  : undefined
              }
              onQueryChange={setSearch}
            />
          ) : null}
        </div>
        <div className={styles['actions']}>
          {isTablet ? <ThemeToggle /> : null}
          <NotificationBell api={api} className={styles['iconButton']} />
          <UserMenu />
        </div>
      </header>
      <main className={styles['main']}>
        <div className={styles['page']}>
          <Outlet />
        </div>
      </main>
      {!isTablet ? (
        <nav className={styles['bottomNav']} aria-label={t('shell.menu')} data-testid="bottom-nav">
          {bottomNavigation(entries).map((item) => (
            <NavItemLink
              key={item.id}
              item={item}
              badges={badges}
              labelKey={item.shortLabelKey ?? item.labelKey}
            />
          ))}
        </nav>
      ) : null}
    </div>
  );
};
